import { useEffect, useContext } from "react";

import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { LogoV2, LogoV3, LogoBrandV3 } from "@/libraries/wpl/foundations/src";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useScrollTracking } from "@/utils/scroll-tracking";

import { WithRequired } from "../../../../../utils/typescript-utils";

import { TeamType, ScrollContext } from "./scroll-pinned-sections";

export const HomepageSectionScrollItem = (
    props: WithRequired<TeamType, "index">
) => {
    const intl = useIntl();
    const gwp7518NavIconUpdates = checkFeatureFlagGate(
        featureFlags.gwp7518NavIconUpdates
    );

    const { ref: scrollRef, inView: sectionIsVisible } = useInView({
        threshold: 0.1,
        rootMargin: "-40% 0px -40% 0px"
    });
    const getItFreeText = intl.formatMessage({
        id: "HomePage.ScrollPinnedSection.getItFreeText",
        defaultMessage: "Get it free"
    });
    const {
        setActiveAccentImg,
        setActiveColor,
        setActiveProductCTAText,
        setActiveProductCTAUrl,
        setActiveShape,
        setActiveFeatureVideo,
        setActivePosterImage,
        setActiveIndex
    } = useContext(ScrollContext);
    useEffect(() => {
        if (sectionIsVisible) {
            setActiveAccentImg(props.accentImg);
            setActiveColor(props.bgColor);
            setActiveProductCTAText(props.productCTA.text);
            setActiveProductCTAUrl(props.productCTA.url);
            setActiveIndex(props.index || 0);
            setActiveShape(props.bgShape);
            setActiveFeatureVideo(props.featureVideo);
            setActivePosterImage(props.posterImage);
        }
    });

    const brandDimensions = { width: 68, height: 68 };
    const posterImageDimensions = { width: 1920, height: 1080 };
    const shouldRenderCustomerCTA =
        props.customerCTAUrl && !props.productCTA.text.includes("Loom");

    const renderSummary = (summary: string) => {
        if (props.customerCTAUrl && props.productCTA.text.includes("Loom")) {
            return (
                <>
                    &quot;{summary}&quot; <br />
                    <span className="name">- {props.name}</span>.{" "}
                    <span className="title">{props.title}</span>
                </>
            );
        }
        const firstWord = summary.split(" ")[0] || "";
        const restOfSummary = summary.substring(firstWord.length);
        return (
            <>
                <span className="first-word">{firstWord}</span>
                {restOfSummary}
            </>
        );
    };
    const { ref } = useScrollTracking(
        "ScrollPinnedSections_" + props.productLogoName
    );

    return (
        <UFOSegment name="homepage-section-scroll-item">
            <div className="team-type" css={teamTypeStyles} ref={ref}>
                <div
                    className={`team-card ${
                        sectionIsVisible ? "active" : "inactive"
                    }`}
                    css={teamCardStyles}
                    ref={scrollRef}
                >
                    <div css={[logoStyles]}>
                        {gwp7518NavIconUpdates ? (
                            <LogoV3
                                height="24"
                                brand={
                                    props.productLogoName === "loom"
                                        ? "loomcircular"
                                        : (props.productLogoName as LogoBrandV3)
                                }
                                type="logo"
                                appearance="brand"
                            />
                        ) : (
                            <LogoV2
                                width="min-content"
                                brand={props.productLogoName}
                                type="logo"
                                appearance="brand"
                            />
                        )}
                    </div>
                    <h3 css={h3Styles}>{props.header}</h3>
                    <p className="body-lg" css={descriptionStyles}>
                        {props.description}
                    </p>
                    {props.productCTA && (
                        <div
                            css={exploreProductWrapperMobile}
                            className="mobile"
                        >
                            <Adg4Button
                                css={ctaButtonStyles}
                                className="cta-button"
                                variant="primary"
                                color="blue"
                                href={props.productCTA.tryCloudUrl}
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "scrollPinnedSection",
                                    "data-label": getItFreeText,
                                    "data-label-english": getItFreeText
                                }}
                            >
                                {getItFreeText}
                            </Adg4Button>
                            <Link
                                text={props.productCTA.text}
                                href={props.productCTA.url}
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "scrollPinnedSection",
                                    "data-label": props.productCTA?.text,
                                    "data-label-english": props.productCTA?.text
                                }}
                                type={"primary"}
                            />
                        </div>
                    )}
                    {props.productCTA && (
                        <div
                            css={exploreProductWrapperDesktop}
                            className="desktop"
                        >
                            <Adg4Button
                                css={ctaButtonStyles}
                                className="cta-button"
                                variant="primary"
                                color="blue"
                                href={props.productCTA.tryCloudUrl}
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "scrollPinnedSection",
                                    "data-label": getItFreeText,
                                    "data-label-english": getItFreeText
                                }}
                            >
                                {getItFreeText}
                            </Adg4Button>
                            <Link
                                text={props.productCTA.text}
                                href={props.productCTA.url}
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "scrollPinnedSection",
                                    "data-label": props.productCTA?.text,
                                    "data-label-english": props.productCTA?.text
                                }}
                                type={"primary"}
                            />
                        </div>
                    )}
                    <div css={quoteStyles}>
                        <div css={brandStyles}>
                            <Picture
                                image={{
                                    src: props.brand,
                                    alt: props.title,
                                    width: brandDimensions.width,
                                    height: brandDimensions.height
                                }}
                            />
                        </div>
                        <p className="body-sm" css={quotePStyles}>
                            {renderSummary(props.summary)}{" "}
                            {shouldRenderCustomerCTA && (
                                <Link
                                    text={props.customerCTA}
                                    href={props.customerCTAUrl}
                                    css={customerCTALinkStyles}
                                    dataEventProps={{
                                        "data-product-key": "home",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container":
                                            "scrollPinnedSection",
                                        "data-label": props.customerCTA,
                                        "data-label-english":
                                            props.productCTA.text
                                    }}
                                    type={"inline"}
                                />
                            )}
                        </p>
                    </div>
                </div>
                <div className="img-region" css={imgRegionStyles}>
                    <div
                        className="bg-shape"
                        css={bgShapeStyles}
                        style={{
                            clipPath: `polygon(${props.bgShape})`,
                            backgroundColor: `#${props.bgColor}`
                        }}
                    />
                    <div className="img-accent" css={imgAccentStyles}>
                        {props.accentImg}
                    </div>
                    {}
                    <div css={imgScreenshotStyles}>
                        <Picture
                            image={{
                                src: props.posterImage,
                                alt: "",
                                className: "img-screenshot",
                                width: posterImageDimensions.width,
                                height: posterImageDimensions.height
                            }}
                        />
                    </div>
                </div>
            </div>
        </UFOSegment>
    );
};

const teamTypeStyles = css({
    position: "relative",
    display: "flex",
    marginBlockEnd: "200px",

    [media.below.md]: {
        marginBlockEnd: "40px",
        flexDirection: "column",
        alignItems: "center"
    }
});

const teamCardStyles = css({
    [media.below.md]: {
        opacity: 1,
        alignItems: "center"
    }
});

const imgRegionStyles = css({
    display: "none",
    position: "relative",
    aspectRatio: "7/4",
    width: "100%",
    padding: "15px",
    margin: "30px auto 40px",

    [media.below.md]: {
        display: "block",
        alignItems: "center"
    }
});

const bgShapeStyles = css({
    width: "100%",
    height: "100%",
    transition: "all 1s",
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: 0
});

const imgAccentStyles = css({
    width: "20%",
    position: "absolute",
    insetInlineStart: "-3%",
    insetBlockEnd: "-10%"
});

const logoStyles = css({
    height: "24px",
    margin: "0 0 10px 0",
    width: "fit-content",
    [media.below.md]: {
        margin: "0 auto"
    }
});

const h3Styles = css({
    fontSize: "2rem",
    margin: 0,
    [media.below.md]: {
        textAlign: "center"
    }
});
const descriptionStyles = css({
    marginInlineEnd: "30px",
    marginBlockStart: "12px",
    maxWidth: "450px",

    [media.below.md]: {
        marginInlineEnd: "0",
        textAlign: "center"
    }
});
const imgScreenshotStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        position: "relative",
        display: "block",
        height: "auto",
        width: "100%",
        margin: "auto",
        animation: "fadein 0.25s 1",
        borderRadius: "5px",
        border: `3px solid ${wacToken("color.neutral.light200")}`
    }
});

const exploreProductWrapperMobile = css({
    display: "none",

    [media.below.sm]: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: "20px",
        alignItems: "center"
    },
    [media.below.xs]: {
        flexDirection: "column",
        gap: "16px"
    }
});

const exploreProductWrapperDesktop = css({
    display: "flex",
    flexDirection: "row",
    gap: "20px",
    alignItems: "center",

    [media.below.sm]: {
        display: "none"
    },
    [media.below.md]: {
        alignItems: "center",
        justifyContent: "center"
    }
});

const ctaButtonStyles = css({
    whiteSpace: "nowrap",
    fontSize: "16px",
    minHeight: "36px",
    lineHeight: "20px",
    paddingBlockStart: "6px",
    paddingBlockEnd: "6px"
});

const quoteStyles = css({
    display: "flex",
    alignItems: "center",
    marginBlockStart: "32px",
    backgroundColor: wacToken("color.neutral.light0"),
    padding: "30px 20px",
    position: "relative",
    borderRadius: "20px",
    boxShadow: `0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25)`,
    gap: "16px",
    maxWidth: "450px"
});

const brandStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "68px !important",
        height: "68px !important",
        marginInlineEnd: "68px",
        objectFit: "contain"
    }
});
const quotePStyles = css({
    "margin": "0",
    "fontStyle": "normal",
    "color": wacToken("color.primary.black"),
    "fontSize": "16px !important",
    "lineHeight": "24px !important",
    "display": "inline",
    "fontWeight": "700",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& .first-word": {
        fontWeight: "bold"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& .name": {
        fontWeight: "bold"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& .title": {
        fontWeight: "bold"
    }
});
const customerCTALinkStyles = css({
    "fontSize": "16px",
    "color": wacToken("color.primary.blue"),
    "&:hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0056b3"
    },
    "display": "inline"
});
